/**
 * Agrupa linhas flat (produto × tamanho × local) em rows master-detail.
 * Usado como fallback quando o endpoint agrupado não está disponível.
 */

const maxTimestamp = (a, b) => {
  const ta = a != null ? Number(a) : 0;
  const tb = b != null ? Number(b) : 0;
  return Math.max(ta, tb);
};

export const computeQuantidadeNoLocal = (variantes, localFilter) => {
  if (!localFilter || !Array.isArray(variantes)) return undefined;
  return variantes.reduce((sum, v) => {
    if (v.local === localFilter) {
      return sum + (Number(v.quantidade) || 0);
    }
    return sum;
  }, 0);
};

const aggregateProduct = (product, localFilter) => {
  const { variantes } = product;
  const locais = new Set(variantes.map((v) => v.local).filter(Boolean));
  const tamanhos = new Set(
    variantes.map((v) => v.produtoTamanhoNome).filter(Boolean)
  );

  let quantidadeTotal = 0;
  let dataUltimaMovimentacao = 0;

  variantes.forEach((v) => {
    quantidadeTotal += Number(v.quantidade) || 0;
    const movement = maxTimestamp(v.dataUltimaEntrada, v.dataUltimaSaida);
    if (movement > dataUltimaMovimentacao) {
      dataUltimaMovimentacao = movement;
    }
  });

  const quantidadeNoLocal = computeQuantidadeNoLocal(variantes, localFilter);

  return {
    ...product,
    quantidadeTotal,
    quantidadeNoLocal,
    locaisCount: locais.size,
    tamanhosCount: tamanhos.size,
    dataUltimaMovimentacao: dataUltimaMovimentacao || null,
    variantes,
  };
};

/**
 * @param {Array} flatRows - resposta de produtos/estoques/locais/filter
 * @param {string} [localFilter] - local do filtro ativo
 * @returns {Array} rows agrupadas por produto
 */
export const groupEstoqueFlatRows = (flatRows, localFilter) => {
  if (!Array.isArray(flatRows) || flatRows.length === 0) {
    return [];
  }

  const byProduct = new Map();

  flatRows.forEach((row) => {
    const key = row.produtoId != null ? String(row.produtoId) : `${row.produtoCodigo}-${row.produtoNome}`;
    if (!byProduct.has(key)) {
      byProduct.set(key, {
        id: row.produtoId != null ? row.produtoId : key,
        produtoId: row.produtoId,
        produtoNome: row.produtoNome,
        produtoCodigo: row.produtoCodigo,
        variantes: [],
      });
    }
    const product = byProduct.get(key);
    const matchesLocal = Boolean(localFilter && row.local === localFilter);
    product.variantes.push({
      id: row.id,
      produtoTamanhoNome: row.produtoTamanhoNome,
      local: row.local,
      quantidade: row.quantidade,
      dataUltimaEntrada: row.dataUltimaEntrada,
      dataUltimaSaida: row.dataUltimaSaida,
      localFiltrado: matchesLocal,
    });
  });

  return Array.from(byProduct.values()).map((p) => aggregateProduct(p, localFilter));
};

/**
 * Enriquece rows já agrupadas vindas da API com quantidadeNoLocal e localFiltrado.
 */
export const normalizeGroupedEstoqueRows = (rows, localFilter) => {
  if (!Array.isArray(rows)) return [];
  if (!localFilter) return rows;

  return rows.map((row) => ({
    ...row,
    quantidadeNoLocal:
      row.quantidadeNoLocal != null
        ? row.quantidadeNoLocal
        : computeQuantidadeNoLocal(row.variantes, localFilter),
    variantes: (row.variantes || []).map((v) => ({
      ...v,
      localFiltrado: v.localFiltrado ?? v.local === localFilter,
    })),
  }));
};
